import { BCTabItemWithIcon, BCTabs, ITabsItem } from '@bimser/components';
import { FormItemTypes, IDictionary, MLHelper } from '@bimser/core';
import IconEmpty from '@bimser/icons/16/empty-icon';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import React from 'react';
import { IPropertyField, ITabItem } from '../../../../entities/common';
import { IUserEvent } from '../../../../entities/services';
import ContainerControl from '../../base/ContainerControl';
import { ITabMenuItemClickEventArgs } from '../../entities/EventArgs';
import Styles from './assets/tabsStyles.scss';
import { ITabMenuProperties } from './entities/ITabMenuProperties';

export default class TabMenu extends ContainerControl {
	tabItems: ITabItem<IDictionary<string>>[];
	hasScrollbar: boolean;

	get selectedItem(): ITabItem<IDictionary<string>> {
		return this.tabItems.find(({ selected }) => selected);
	}

	set selectedItem(item: ITabItem<IDictionary<string>>) {
		this.__proxyChanging.tabItems = this.tabItems.map(i => ({
			...i,
			selected: i.key === item.key
		}));
	}

	get selectedIndex(): number {
		return this.tabItems.findIndex(({ selected }) => selected);
	}

	set selectedIndex(index: number) {
		this.tabItems[index].selected = true;
	}

	constructor() {
		super();
		this.controlType = FormItemTypes.EControlTypes.TabMenu;
		this.caption.visible = false;
		this.tabItems = [];
		this.onChange = this.onChange.bind(this);
		this.onTabClick = this.onTabClick.bind(this);

		this.subscribe('trigger:OnValueChanged', (value: string) => {
			this.onChange(value);
			this.onTabClick(value);
		});
	}

	__loadProperties(data: ITabMenuProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.tabItems = data.tabItems;
	}

	private getTabItems(): ITabsItem[] {
		return [...this.tabItems]
			.sort((a, b) => a.orderNo - b.orderNo)
			.map(item => ({
				key: item.key,
				disabled: !item.enabled || this.__isDisabled() || this.__isReadOnly(),
				label: BCTabItemWithIcon({
					label: MLHelper.getMLText(item.title),
					icon: GetIcon16ByName(item.icon, IconEmpty).info
				})
			}));
	}

	__getPropertyFields(): IPropertyField[] {
		return [
			...super.__getPropertyFields(),
			{
				name: 'selectedIndex',
				type: 'number',
				common: true
			},
			{
				name: 'selectedItem',
				type: 'object',
				items: [
					{
						name: 'selected',
						type: 'boolean'
					},
					{
						name: 'icon',
						type: 'string'
					},
					{
						name: 'orderNo',
						type: 'number'
					},
					{
						name: 'enabled',
						type: 'boolean'
					},
					{
						name: 'key',
						type: 'string',
						readOnly: true,
						common: true
					}
				]
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			tabItems: this.tabItems,
			selectedIndex: this.selectedIndex,
			hasScrollbar: this.hasScrollbar
		};
	}

	onChange(e: string) {
		this.selectedItem = this.tabItems.find(item => item.key === e);
	}

	onTabClick(name: string, e?: React.MouseEvent<HTMLDivElement, MouseEvent>) {
		if (e) {
			this.__onControlClick(e);
		}

		const argument: ITabMenuItemClickEventArgs = {
			name: name
		};

		this.emit<ITabMenuItemClickEventArgs>('event:OnTabMenuItemClick', argument);
		this.emit<IUserEvent<ITabMenuItemClickEventArgs>>('event:server:OnTabMenuItemClick', {
			argument,
			controlId: this.name,
			name: 'TabMenuItemClick',
			sender: 'web'
		});
	}

	__render() {
		return super.__render(
			<BCTabs
				items={this.getTabItems()}
				cssClass={Styles.tabs}
				defaultActiveKey={this.selectedItem?.key}
				activeKey={this.selectedItem?.key}
				tabBarStyle={{
					height: 50,
					backgroundColor: this.style.backgroundColor
				}}
				onChange={this.onChange}
				onTabClick={this.onTabClick}
				readOnly={this.__isReadOnly()}
			/>
		);
	}
}
